import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { CraftGlobals } from 'tsconfig/craft-types'
import { useEffect, useContext } from 'react'
import {
  CSeomatic,
  SeomaticData,
  CFindADoctor,
  CCenteredCopy,
  CTestimonialMastheadFragments,
  CSimpleCopyFragments,
  CFullWidthVideoFragments,
  CStoryGalleryFragments,
  CImageSectionBreakFragments,
  CQuestionsAndAnswersFragments,
  CRelatedArticlesFragmets,
  CTestimonialMastheadBlock,
  CSimpleCopyBlock,
  CFullWidthVideoBlock,
  CStoryGalleryBlock,
  CImageSectionBreakBlock,
  CQuestionsAndAnswersBlock,
  CRelatedArticlesBlock,
  CTestimonialMasthead,
  CSimpleCopy,
  CFullWidthVideo,
  CStoryGallery,
  CImageSectionBreak,
  CQuestionsAndAnswers,
  CRelatedArticles,
} from 'ui'
import { GetServerSidePropsContext } from 'next'
import { HeaderContext } from 'ui/context/header'
import { useTranslation } from 'react-i18next'
import { gql } from '@urql/core'
import { Provider } from 'urql'
import { getSiteInfo, useSiteInfo } from 'shared'
import { defaultClient } from '../../graphql/clients'
import fragments from '../../helpers/gql-fragments'
import DefaultLayout from '../../layouts/default'
import getGlobals from '../../graphql/get-globals'
import { getI18nResolvedUrl } from '../../helpers/locale-tools'
import { setCacheHeaders } from '../../helpers/cache-config'

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const siteInfo = getSiteInfo(context.locale ?? '')

  const global = await getGlobals(siteInfo.handle)

  const { data } = await defaultClient(siteInfo.handle).query(
    gql`
      query($uri: [String]!, $site: [String]!) {
        entry(uri: $uri, site: $site) {
          ${fragments.seomatic}
          id
          uri
          sectionHandle
          typeHandle
          title
          ... on testimonialPages_testimonialPages_Entry {
            testimonialMasthead {
              ...testimonialMasthead
            }
            simpleCopy {
              ...simpleCopy
            }
            fullWidthVideo {
              ...fullWidthVideo
            }
            storyGallery {
              ...storyGallery
            }
            simpleCopy2 {
              ...simpleCopy2
            }
            imageSectionBreak {
              ...imageSectionBreak
            }
            questionsAndAnswers {
              ...questionsAndAnswers
            }
            relatedArticles {
              ...relatedArticles
            }
          }
        }
      }
      ${CTestimonialMastheadFragments.field}
      ${CSimpleCopyFragments.field}
      ${CFullWidthVideoFragments.field}
      ${CStoryGalleryFragments.field}
      ${CSimpleCopyFragments.field2}
      ${CImageSectionBreakFragments.field}
      ${CQuestionsAndAnswersFragments.field}
      ${CRelatedArticlesFragmets.field}
    `,
    {
      uri: getI18nResolvedUrl(context.req.url ?? ''),
      site: siteInfo.handle,
    }
  )

  if (!data.entry) {
    return {
      notFound: true,
    }
  }

  setCacheHeaders(context.res)

  return {
    props: {
      ...(await serverSideTranslations(context.locale ?? '', ['common'])),
      entry: data.entry,
      global,
    },
  }
}

type Props = {
  entry: {
    id?: string
    title?: string
    seomatic: SeomaticData
    testimonialMasthead: Array<CTestimonialMastheadBlock>
    simpleCopy: Array<CSimpleCopyBlock>
    fullWidthVideo: Array<CFullWidthVideoBlock>
    storyGallery: Array<CStoryGalleryBlock>
    simpleCopy2: Array<CSimpleCopyBlock>
    imageSectionBreak: Array<CImageSectionBreakBlock>
    questionsAndAnswers: Array<CQuestionsAndAnswersBlock>
    relatedArticles: Array<CRelatedArticlesBlock>
    typeHandle: string
  }
  global: CraftGlobals
}

export default function TestimonialPage({ entry, global }: Props) {
  const siteInfo = useSiteInfo()
  const { t } = useTranslation('common')
  const headerContext = useContext(HeaderContext)
  const displayVideoOnTop = Boolean(
    entry.fullWidthVideo?.[0]?.position === 'top'
  )

  useEffect(() => {
    headerContext?.setHasProgressBar(true)

    return () => {
      headerContext?.setHasProgressBar(false)
    }
  }, [])

  return (
    <DefaultLayout global={global}>
      <CSeomatic data={entry.seomatic} />
      <CTestimonialMasthead
        field={entry.testimonialMasthead}
        name={entry.title}
        displayImage={!displayVideoOnTop}
      />
      {displayVideoOnTop && (
        <CFullWidthVideo
          field={entry.fullWidthVideo}
          testimonialName={entry.title}
          form={global.testimonialsform}
        />
      )}
      <CSimpleCopy
        field={entry.simpleCopy}
        parentTypeHandle={siteInfo.isUS ? entry.typeHandle : ''}
      />
      {!displayVideoOnTop && (
        <CFullWidthVideo
          field={entry.fullWidthVideo}
          testimonialName={entry.title}
          form={global.testimonialsform}
        />
      )}
      <CStoryGallery field={entry.storyGallery} />
      <CSimpleCopy field={entry.simpleCopy2} />
      <CImageSectionBreak field={entry.imageSectionBreak} />
      <CQuestionsAndAnswers field={entry.questionsAndAnswers} />
      <CFindADoctor field={global.findADoctor} />
      <Provider value={defaultClient()}>
        <CRelatedArticles
          field={entry.relatedArticles}
          excludeId={parseInt(entry.id ?? '', 10) ?? 0}
          loadMoreLabel={t('load-more') ?? ''}
        />
      </Provider>
      <CCenteredCopy field={global.centeredCopy} />
    </DefaultLayout>
  )
}
